import React from 'react'
import { FlaskConical } from 'lucide-react'
import GeoGebraSimulation from '../../../components/interactive/shared/simulations/GeoGebraSimulation'
import { GeoGebraService } from '../../../services/video/GeoGebraService'
import PyodideRunner from '../components/PyodideRunner'
import { genId } from '../lib/id'
import type { SimulationCard } from '../types/LessonDoc'
import type { CardDef, CardEditorProps, CardPreviewProps } from './types'

const PROJECTILE_EXAMPLE = `import numpy as np
import matplotlib.pyplot as plt

g = 9.8        # gia tốc trọng trường (m/s^2)
v0 = 20        # vận tốc đầu (m/s)
angle = 45     # góc ném (độ)

a = np.radians(angle)
t_flight = 2 * v0 * np.sin(a) / g
t = np.linspace(0, t_flight, 200)
x = v0 * np.cos(a) * t
y = v0 * np.sin(a) * t - 0.5 * g * t**2

plt.figure(figsize=(6, 4))
plt.plot(x, y)
plt.title('Quỹ đạo chuyển động ném xiên')
plt.xlabel('x (m)'); plt.ylabel('y (m)')
plt.grid(True)

print('Tầm xa:', round(x[-1], 2), 'm')
print('Độ cao cực đại:', round(y.max(), 2), 'm')
`

const Editor: React.FC<CardEditorProps> = ({ card, onChange }) => {
  const c = card as SimulationCard
  const ggbList = GeoGebraService.getAll()

  return (
    <div className="space-y-3">
      <div>
        <label className="text-xs font-bold text-gray-500 uppercase">Loại mô phỏng</label>
        <select
          className="w-full p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200 bg-white"
          value={c.kind}
          onChange={(e) => onChange({ kind: e.target.value as SimulationCard['kind'] })}
        >
          <option value="geogebra">GeoGebra (chạy trực tiếp)</option>
          <option value="python">Python (Pyodide)</option>
        </select>
      </div>

      {c.kind === 'geogebra' ? (
        <>
          <div>
            <label className="text-xs font-bold text-gray-500 uppercase">Chọn mô phỏng GeoGebra có sẵn</label>
            <select
              className="w-full p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200 bg-white"
              value={c.ggbFile || ''}
              onChange={(e) => onChange({ ggbFile: e.target.value || undefined, materialId: undefined })}
            >
              <option value="">— Chọn từ thư viện —</option>
              {ggbList.map((g) => (
                <option key={g.id} value={g.filename}>
                  {g.title} (L{g.lop}·C{g.chuong}·B{g.bai})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold text-gray-500 uppercase">hoặc GeoGebra material id</label>
            <input
              className="w-full p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200"
              value={c.materialId || ''}
              onChange={(e) => onChange({ materialId: e.target.value || undefined, ggbFile: undefined })}
              placeholder="vd: abcd1234 (nếu không dùng file .ggb)"
            />
          </div>
        </>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-xs font-bold text-gray-500 uppercase">Mã Python (matplotlib/numpy)</label>
            <button
              type="button"
              onClick={() => onChange({ code: PROJECTILE_EXAMPLE })}
              className="text-xs font-bold text-violet-600 hover:text-violet-700"
            >
              Chèn ví dụ ném xiên
            </button>
          </div>
          <textarea
            className="w-full min-h-[200px] p-3 border border-gray-200 rounded-lg text-xs font-mono outline-none focus:ring-2 focus:ring-blue-200"
            value={c.code || ''}
            onChange={(e) => onChange({ code: e.target.value })}
            placeholder="Dùng matplotlib.pyplot để vẽ — kết quả hiện ở phần xem trước khi bấm Chạy."
          />
          <p className="text-[11px] text-gray-400">Hỗ trợ numpy + matplotlib. Dùng plt.plot(...) → hình hiện ở xem trước.</p>
        </div>
      )}
    </div>
  )
}

const Preview: React.FC<CardPreviewProps> = ({ card }) => {
  const c = card as SimulationCard

  if (c.kind === 'python') {
    if (!c.code) {
      return <div className="p-4 rounded-lg bg-gray-50 border border-dashed border-gray-200 text-gray-400 text-sm text-center">Chưa có mã Python.</div>
    }
    return <PyodideRunner code={c.code} />
  }

  // GeoGebra
  if (c.ggbFile) {
    return (
      <GeoGebraSimulation
        filename={GeoGebraService.getPublicPath(c.ggbFile)}
        title={c.title || 'Mô phỏng GeoGebra'}
        height={c.height ?? 520}
        showCustomControls
      />
    )
  }
  if (c.materialId) {
    return (
      <iframe
        title={c.title || 'Mô phỏng GeoGebra'}
        src={`https://www.geogebra.org/material/iframe/id/${c.materialId}`}
        className="w-full rounded-lg border border-gray-200 bg-white"
        style={{ height: c.height ?? 480 }}
        allowFullScreen
      />
    )
  }
  return (
    <div className="p-4 rounded-lg bg-gray-50 border border-dashed border-gray-200 text-gray-400 text-sm text-center">
      Chọn mô phỏng GeoGebra hoặc nhập material id.
    </div>
  )
}

export const simulationCardDef: CardDef = {
  type: 'simulation',
  label: 'Mô phỏng',
  description: 'GeoGebra trực tiếp / Python',
  icon: FlaskConical,
  color: 'bg-violet-50 text-violet-600',
  createDefault: (): SimulationCard => ({ id: genId(), type: 'simulation', kind: 'geogebra', height: 520 }),
  Editor,
  Preview,
}
